/**
 * Add Vehicle Type Modal Component
 *
 * Modal for creating new OTA vehicle types with name and description.
 */

import React, { memo, useState } from "react";
import { toast } from "react-toastify";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  TextArea,
  Button,
} from "./styles";

interface AddVehicleTypeModalProps {
  onClose: () => void;
  onSubmit: (data: { name: string; description: string }) => Promise<void>;
}

export const AddVehicleTypeModal = memo<AddVehicleTypeModalProps>(
  ({ onClose, onSubmit }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!name.trim()) {
        toast.error("Vehicle type name is required");
        return;
      }

      try {
        setLoading(true);
        await onSubmit({ name: name.trim(), description: description.trim() });
        setLoading(false);
      } catch (err: any) {
        // Error is handled by parent component
        setLoading(false);
      }
    };

    return (
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Add New Vehicle Type</ModalTitle>
            <ModalCloseButton onClick={onClose}>×</ModalCloseButton>
          </ModalHeader>

          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormGroup>
                <Label htmlFor="name">
                  Name <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setName(e.target.value)
                  }
                  placeholder="e.g., AMR-500, Forklift-X"
                  autoFocus
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">Description</Label>
                <TextArea
                  id="description"
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setDescription(e.target.value)
                  }
                  placeholder="Detailed description of the vehicle type..."
                  rows={4}
                  disabled={loading}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? "Creating..." : "Create"}
              </Button>
            </ModalFooter>
          </form>
        </ModalContainer>
      </ModalOverlay>
    );
  }
);
